import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Mail, Send, ExternalLink, RefreshCw, XCircle } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk } from "@/lib/queryClient";
import { toast } from "sonner";
import DemiQuotes, { McpBadge } from "@/pages/DemiQuotes";


/**
 * CC-60 — EmailPreviewInline (quotes variant)
 * Fetches the composed HTML from GET /api/weekly-quotes-v2/{id}/preview-email
 * and drops it into a sandboxed iframe (srcDoc) with `allow-same-origin` only
 * (no `allow-scripts`). Used by the inline Dashboard/Email toggle.
 */
function EmailPreviewInline({ briefId }) {
  const [html, setHtml] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setErr(null);
    (async () => {
      try {
        const r = await fetch(`${API}/weekly-quotes-v2/${briefId}/preview-email`, {
          credentials: "include",
          headers: { ...authHeaders() },
        });
        if (!r.ok) {
          const t = await r.text().catch(() => "");
          throw new Error(`HTTP ${r.status} · ${t.slice(0, 160)}`);
        }
        const text = await r.text();
        if (!cancelled) setHtml(text);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load email preview");
      }
    })();
    return () => { cancelled = true; };
  }, [briefId]);
  if (err) {
    return (
      <div className="hm-surface p-6 text-center" data-testid="quotes-email-preview-inline-error">
        <XCircle className="h-6 w-6 text-hm-danger mx-auto mb-2" />
        <div className="text-sm text-hm-fg">Could not render email preview.</div>
        <p className="mono text-[11px] text-hm-fg-muted mt-1 break-all">{err}</p>
      </div>
    );
  }
  if (html === null) {
    return (
      <div className="hm-surface p-10 text-center" data-testid="quotes-email-preview-inline-loading">
        <Loader2 className="h-6 w-6 text-hm-fg-subtle mx-auto mb-2 animate-spin" />
        <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">Rendering email…</div>
      </div>
    );
  }
  return (
    <div className="hm-surface overflow-hidden" data-testid="quotes-email-preview-inline"
         style={{ background: "#F5F6F8" }}>
      <iframe
        title="Email preview"
        srcDoc={html}
        sandbox="allow-same-origin"
        data-testid="quotes-email-preview-iframe"
        className="w-full border-0"
        style={{ minHeight: 720, background: "#F5F6F8" }}
      />
    </div>
  );
}


function StatusPill({ status }) {
  const map = {
    review: { bg: "#EFF6FF", fg: "#1D4ED8", label: "Review" },
    sent:   { bg: "#ECFDF5", fg: "#047857", label: "Sent" },
  };
  const m = map[status] || { bg: "#F1F5F9", fg: "#475569", label: status };
  return (
    <span
      className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ background: m.bg, color: m.fg }}
    >{m.label}</span>
  );
}


function DeliveryLine({ latest }) {
  if (!latest) {
    return (
      <div className="mono text-[11px] text-hm-fg-subtle" data-testid="quotes-delivery-status">
        Not delivered yet.
      </div>
    );
  }
  const when = new Date(latest.sent_at).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const via = latest.trigger === "auto_cron" ? "automated" : latest.trigger;
  return (
    <div className="mono text-[11px] text-hm-fg-muted" data-testid="quotes-delivery-status">
      Delivered to <span className="text-hm-fg">{(latest.to_emails || []).join(", ")}</span>
      {" · "}{when}{" · "}<span className="text-hm-fg">{via}</span>
    </div>
  );
}


function QuoteCard({ q }) {
  const tc = [q.speaker_title, q.speaker_company].filter(Boolean).join(" · ");
  const srcHost = (() => {
    if (!q.source_url) return "";
    try { return new URL(q.source_url).host.replace(/^www\./, ""); }
    catch { return ""; }
  })();
  return (
    <div className="hm-surface p-4 flex flex-col" data-testid={`quote-card-${q.rank}`}>
      <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-3">
        Rank #{q.rank} · Score {q.shareability_score}
        {q.customer_tier ? ` · Tier ${q.customer_tier}` : ""}
      </div>
      {q.image_url ? (
        <img
          src={q.image_url}
          alt={`Quote ${q.rank}`}
          className="w-full rounded-md mb-3"
          style={{ display: "block", aspectRatio: "1080/1350", objectFit: "cover" }}
          data-testid={`quote-image-${q.rank}`}
        />
      ) : (
        <div className="w-full mb-3 rounded-md flex items-center justify-center"
             style={{ aspectRatio: "1080/1350", background: "#0A0E4A", color: "white" }}>
          <span className="mono text-[10px] uppercase tracking-widest">Image pending</span>
        </div>
      )}
      <div className="text-sm text-hm-fg font-semibold">{q.speaker_name}</div>
      <div className="mono text-[11px] text-hm-fg-muted mt-1">{tc}</div>
      {q.quote_text && (
        <div className="text-sm text-hm-fg-muted mt-3 italic leading-relaxed">
          &ldquo;{q.quote_text}&rdquo;
        </div>
      )}
      {q.source_url && (
        <a
          href={q.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mono text-[11px] text-hm-accent hover:underline mt-3 inline-flex items-center gap-1"
          data-testid={`quote-source-${q.rank}`}
        >
          <ExternalLink className="h-3 w-3" />
          {srcHost || "source"}
        </a>
      )}
    </div>
  );
}


export default function WeeklyQuotesDetail() {
  const { id } = useParams();
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [viewMode, setViewMode] = useState("dashboard");
  const queryClient = useQueryClient();

  const briefQ = useQuery({
    queryKey: qk.weeklyQuotesDetail(id),
    queryFn: () => fetchers.weeklyQuotesDetail(id),
    staleTime: 5 * 60_000,
    enabled: !!id,
  });
  const sendsQ = useQuery({
    queryKey: qk.weeklyQuotesSendHistory(id),
    queryFn: () => fetchers.weeklyQuotesSendHistory(id),
    staleTime: 60_000,
    refetchInterval: 30_000,
    enabled: !!id,
  });

  const brief = briefQ.data;
  const sends = sendsQ.data?.sends || [];
  const error = briefQ.error;

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API}/weekly-quotes-v2/${id}/send-email`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ to: ["lenka.galinie@gmail.com"] }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      toast.success(`Sent · ${body.gmail_message_id}`);
      queryClient.invalidateQueries({ queryKey: qk.weeklyQuotesSendHistory(id) });
      queryClient.invalidateQueries({ queryKey: qk.weeklyQuotesDetail(id) });
    } catch (e) {
      toast.error(`Send failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  if (error && !brief) {
    return <div className="hm-surface p-6 text-hm-danger">{error.message || String(error)}</div>;
  }
  if (!brief) {
    return <div className="hm-surface p-6 mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">Loading…</div>;
  }

  const quotes = brief.quotes || [];
  const weekLabel = brief.week_label;
  const latestOk = sends.find(s => s.status === "sent");
  const isDemi = (brief.pipeline_version || "").toLowerCase().startsWith("claude_pro_mcp");

  return (
    <div data-testid="weekly-quotes-detail-page">
      <PageHeader
        title={`Weekly Quotes · ${weekLabel}`}
        subtitle={`${brief.week_start} → ${brief.week_end}`}
      />
      <div className="mb-4"><DeliveryLine latest={latestOk} /></div>

      <div className="hm-surface p-6 mb-6" data-testid="quotes-hero">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-hm-fg">{weekLabel}</h1>
            <div className="mono text-[11px] text-hm-fg-subtle mt-2">
              {brief.quote_count} quotes · {brief.pipeline_version}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isDemi && <McpBadge />}
            <StatusPill status={brief.status} />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
          <div
            role="tablist"
            aria-label="View mode"
            data-testid="quotes-view-mode-toggle"
            className="inline-flex items-center gap-1 rounded-lg border border-hm-border bg-hm-bg-1 p-1"
          >
            {[
              { id: "dashboard", label: "Dashboard view" },
              { id: "email",     label: "Email preview" },
            ].map((m) => {
              const active = viewMode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`quotes-view-mode-${m.id}`}
                  onClick={() => setViewMode(m.id)}
                  className={`mono uppercase tracking-widest text-[11px] px-3 py-1.5 rounded-md transition-colors ${
                    active ? "bg-hm-accent-soft text-hm-accent-dim"
                           : "text-hm-fg-muted hover:text-hm-fg"
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!isDemi && (
              <>
                <button
                  onClick={() => setShowPreview(true)}
                  data-testid="quotes-preview-email"
                  className="h-9 px-4 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong inline-flex items-center gap-1.5"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Preview email (modal)
                </button>
                <button
                  onClick={sendNow}
                  disabled={sending}
                  data-testid="quotes-send-now"
                  className="h-9 px-4 rounded-md text-xs bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim inline-flex items-center gap-1.5 disabled:opacity-40"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Send now
                </button>
              </>
            )}
            <button
              onClick={() => briefQ.refetch()}
              data-testid="quotes-refresh"
              className="h-9 px-3 rounded-md text-xs text-hm-fg-muted hover:text-hm-fg inline-flex items-center gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {viewMode === "email" ? (
        <EmailPreviewInline briefId={id} />
      ) : isDemi ? (
        <DemiQuotes payload={quotes} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quotes.map((q, i) => (
            <QuoteCard key={q.rank || i} q={q} />
          ))}
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="quotes-email-preview-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-hm-border shrink-0">
              <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">Email preview</div>
              <button
                onClick={() => setShowPreview(false)}
                data-testid="quotes-email-preview-close"
                className="text-hm-fg-muted hover:text-hm-fg text-sm"
              >Close</button>
            </div>
            <iframe
              title="quotes-preview"
              src={`${API}/weekly-quotes-v2/${id}/preview-email`}
              className="flex-1"
              style={{ border: 0 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
